import { createContext, useContext, useCallback, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
} from "@/components/ui/alert-dialog";

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, title: "", description: "", confirmText: "Confirm", destructive: true });
  const resolver = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolver.current = resolve;
      setState({
        open: true,
        title: opts.title || "Are you sure?",
        description: opts.description || "",
        confirmText: opts.confirmText || "Confirm",
        destructive: opts.destructive !== false,
      });
    });
  }, []);

  const close = (result) => {
    setState((s) => ({ ...s, open: false }));
    if (resolver.current) { resolver.current(result); resolver.current = null; }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={state.open} onOpenChange={(o) => { if (!o) close(false); }}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl tracking-tight">{state.title}</AlertDialogTitle>
            {state.description && <AlertDialogDescription>{state.description}</AlertDialogDescription>}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <button onClick={() => close(false)} data-testid="confirm-cancel"
              className="mt-2 sm:mt-0 px-6 py-2 text-xs uppercase tracking-[0.2em] text-[#5C5652] border border-[#EAE7E0] hover:text-[#2A2421]">
              Cancel
            </button>
            <button onClick={() => close(true)} data-testid="confirm-accept"
              className={`px-6 py-2 text-xs uppercase tracking-[0.2em] text-white transition-colors ${state.destructive ? "bg-red-700 hover:bg-red-800" : "bg-[#C88270] hover:bg-[#B37363]"}`}>
              {state.confirmText}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx;
}
